import { useState } from 'react';
import type { AnalysisReport } from '../types/analysis';

interface AnalysisReportViewProps {
  report: AnalysisReport;
}

const SEVERITY_CLASSES: Record<string, string> = {
  high:   'bg-red-100 text-red-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low:    'bg-gray-100 text-gray-600',
};

const PRIORITY_CLASSES: Record<string, string> = {
  high:   'bg-red-100 text-red-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low:    'bg-blue-100 text-blue-700',
};

function SectionHeader({ title, count, open, onToggle }: { title: string; count: number; open: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 rounded-t-lg border border-gray-200 hover:bg-gray-100 transition-colors"
    >
      <span className="font-semibold text-gray-800 text-sm">{title} ({count})</span>
      <span className="text-gray-400 text-xs">{open ? '▲' : '▼'}</span>
    </button>
  );
}

export default function AnalysisReportView({ report }: AnalysisReportViewProps) {
  const [openSections, setOpenSections] = useState({
    gaps: true,
    risks: true,
    questions: true,
    inconsistencies: true,
    nextActions: true,
  });

  const toggle = (key: keyof typeof openSections) =>
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
        <h2 className="text-sm font-semibold text-blue-800 mb-2">Summary</h2>
        <p className="text-sm text-blue-900">{report.summary}</p>
      </div>

      {/* Gaps */}
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <SectionHeader title="Gaps" count={report.gaps.length} open={openSections.gaps} onToggle={() => toggle('gaps')} />
        {openSections.gaps && (
          <div className="p-4 space-y-3 border-t-0 border border-gray-200 border-t-0 rounded-b-lg">
            {report.gaps.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-2">No gaps identified</p>
            ) : (
              report.gaps.map((gap, i) => (
                <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">{gap.area}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${SEVERITY_CLASSES[gap.severity] ?? SEVERITY_CLASSES.low}`}>
                      {gap.severity}
                    </span>
                  </div>
                  <p className="text-sm text-gray-800 mb-1">{gap.description}</p>
                  <p className="text-xs text-gray-600"><span className="font-medium">Recommendation:</span> {gap.recommendation}</p>
                  <button
                    disabled
                    title="Coming in Sprint 2"
                    className="mt-2 text-xs px-3 py-1 bg-white border border-red-200 text-red-400 rounded-md cursor-not-allowed opacity-50"
                  >
                    Generate Artefact
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Risks */}
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <SectionHeader title="Risks" count={report.risks.length} open={openSections.risks} onToggle={() => toggle('risks')} />
        {openSections.risks && (
          <div className="p-4 space-y-3 border border-gray-200 border-t-0 rounded-b-lg">
            {report.risks.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-2">No risks identified</p>
            ) : (
              report.risks.map((risk, i) => (
                <div key={i} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-gray-800">{risk.title}</span>
                    <span className="text-xs text-gray-500 bg-white border border-gray-200 px-2 py-0.5 rounded-full">
                      L: {risk.likelihood} / I: {risk.impact}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600"><span className="font-medium">Mitigation:</span> {risk.mitigation}</p>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Open Questions */}
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <SectionHeader title="Open Questions" count={report.openQuestions.length} open={openSections.questions} onToggle={() => toggle('questions')} />
        {openSections.questions && (
          <div className="p-4 space-y-3 border border-gray-200 border-t-0 rounded-b-lg">
            {report.openQuestions.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-2">No open questions</p>
            ) : (
              report.openQuestions.map((q, i) => (
                <div key={i} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-800 mb-1">{q.question}</p>
                  <p className="text-xs text-gray-600 mb-2">{q.context}</p>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                    Owner: {q.suggestedOwner}
                  </span>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Inconsistencies */}
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <SectionHeader title="Inconsistencies" count={report.inconsistencies.length} open={openSections.inconsistencies} onToggle={() => toggle('inconsistencies')} />
        {openSections.inconsistencies && (
          <div className="p-4 space-y-3 border border-gray-200 border-t-0 rounded-b-lg">
            {report.inconsistencies.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-2">No inconsistencies found</p>
            ) : (
              report.inconsistencies.map((inc, i) => (
                <div key={i} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <span className="text-xs font-semibold text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full">{inc.between}</span>
                  <p className="text-sm text-gray-800 mt-2 mb-1">{inc.description}</p>
                  <p className="text-xs text-gray-600"><span className="font-medium">Recommendation:</span> {inc.recommendation}</p>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Next Actions */}
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        <SectionHeader title="Next Actions" count={report.nextActions.length} open={openSections.nextActions} onToggle={() => toggle('nextActions')} />
        {openSections.nextActions && (
          <div className="p-4 space-y-3 border border-gray-200 border-t-0 rounded-b-lg">
            {report.nextActions.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-2">No next actions</p>
            ) : (
              report.nextActions.map((action, i) => (
                <div key={i} className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${PRIORITY_CLASSES[action.priority] ?? PRIORITY_CLASSES.low}`}>
                      {action.priority}
                    </span>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                      {action.assignee}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-800 mb-1">{action.action}</p>
                  <p className="text-xs text-gray-600">{action.rationale}</p>
                  <button
                    disabled
                    title="Coming in Sprint 2"
                    className="mt-2 text-xs px-3 py-1 bg-white border border-green-200 text-green-400 rounded-md cursor-not-allowed opacity-50"
                  >
                    Generate Task
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
